import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { ExamSchedule, Student } from '@/types';
import ExamItem from './components/ExamItem';

type Tab = 'upcoming' | 'past' | 'all';

const TABS: { key: Tab; label: string; icon: string }[] = [
  { key: 'upcoming', label: '예정된 시험', icon: 'ri-calendar-event-line' },
  { key: 'past', label: '지난 시험', icon: 'ri-history-line' },
  { key: 'all', label: '전체', icon: 'ri-list-check' },
];

interface ExamRow extends ExamSchedule {
  studentName: string;
  isPast: boolean;
}

function dateKey(value?: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.slice(0, 10);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function todayKey(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export default function ExamsPage() {
  const [exams, setExams] = useState<ExamRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<Tab>('upcoming');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [examsRes, studentsRes] = await Promise.all([
        supabase.from('exam_schedules').select('*'),
        supabase.from('students').select('id, name'),
      ]);
      if (examsRes.error) throw examsRes.error;
      if (studentsRes.error) throw studentsRes.error;

      const nameMap = new Map<string, string>();
      ((studentsRes.data as { id: string; name: string }[]) ?? []).forEach((s) => {
        nameMap.set(s.id, s.name);
      });

      const today = todayKey();
      const rows = ((examsRes.data as ExamSchedule[]) ?? []).map((e) => {
        const key = dateKey(e.end_date ?? e.start_date);
        return {
          ...e,
          studentName: nameMap.get(e.student_id) ?? '학생',
          isPast: !!key && key < today,
        };
      });
      rows.sort((a, b) => dateKey(a.start_date).localeCompare(dateKey(b.start_date)));
      setExams(rows);
    } catch {
      setError('시험 일정을 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    let upcoming = 0;
    let past = 0;
    exams.forEach((e) => {
      if (e.isPast) past += 1;
      else upcoming += 1;
    });
    return { upcoming, past, all: exams.length };
  }, [exams]);

  const visible = useMemo(() => {
    if (tab === 'all') return exams;
    if (tab === 'past') return exams.filter((e) => e.isPast).reverse();
    return exams.filter((e) => !e.isPast);
  }, [exams, tab]);

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-10">
      <div>
        <h1 className="text-2xl font-bold text-foreground-950">시험 일정</h1>
        <p className="mt-1.5 text-sm text-foreground-600">
          전체 학생의 시험 일정과 범위를 한 번에 모아보고, 다가오는 시험을 놓치지 않도록 관리해요.
        </p>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors whitespace-nowrap ${
              tab === t.key
                ? 'bg-foreground-900 text-background-50'
                : 'border border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100'
            }`}
          >
            <i className={t.icon}></i>
            {t.label}
            <span className="text-[11px] opacity-80">{counts[t.key]}</span>
          </button>
        ))}
      </div>

      {error && !loading && (
        <div className="mt-6 flex items-center justify-between gap-3 rounded-md border border-accent-200 bg-accent-100/70 px-4 py-2.5 text-sm text-accent-800">
          <span className="flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {error}
          </span>
          <button
            type="button"
            onClick={load}
            className="rounded-md border border-accent-300 px-3 py-1 text-xs font-semibold text-accent-800 hover:bg-accent-100 whitespace-nowrap"
          >
            다시 시도
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24 text-foreground-500">
          <i className="ri-loader-4-line mr-2 animate-spin text-xl"></i>
          불러오는 중...
        </div>
      ) : visible.length === 0 ? (
        <div className="mt-6 flex flex-col items-center gap-3 rounded-xl border border-dashed border-background-300 bg-background-100 py-20">
          <i className="ri-calendar-line text-3xl text-foreground-400"></i>
          <p className="text-sm text-foreground-600">
            {tab === 'past' ? '지난 시험이 없어요.' : '예정된 시험이 없어요.'}
          </p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {visible.map((e) => (
            <ExamItem
              key={e.id}
              studentId={e.student_id}
              studentName={e.studentName}
              title={e.title}
              startDate={e.start_date}
              endDate={e.end_date}
              scope={e.scope}
              isPast={e.isPast}
            />
          ))}
        </div>
      )}
    </div>
  );
}